// src/components/StageDataModal.tsx
// Read-only viewer for a completed stage's stored stage_data — lets a doctor
// (or anyone resuming a visit) click back through the Visit Workflow stepper
// to see what was recorded earlier, without leaving or disrupting whatever
// stage is currently active. Pre-Consult and the other nine stages each get
// a dedicated, colorful summary view (PreConsultSummaryView.tsx /
// StageSummaryView.tsx); a stage key neither of those recognizes (or a
// visit predating this) falls back to the original generic key/value dump.
import PreConsultSummaryView from './PreConsultSummaryView';
import StageSummaryView, { type MinimalVisit, type MinimalPatient } from './StageSummaryView';
import { humanize, isEmptyValue, RenderValue } from '../utils/stageDataRender';

const HAS_SUMMARY_VIEW = new Set(['verify', 'register', 'vitals', 'queue', 'consultation', 'ip', 'pharmacy', 'billing', 'resolve']);

export interface StageEntry {
  stage: string;
  status?: string;
  stage_data: Record<string, any> | null;
  completed_by?: string | null;
  completed_at?: string | null;
  notes?: string | null;
}

export interface StageDataModalProps {
  stageKey: string;
  stageLabel: string;
  stageIcon: string;
  stageEntry: StageEntry | null | undefined;
  visit?: MinimalVisit;
  patient?: MinimalPatient;
  onClose: () => void;
}

/**
 * Just the body — no overlay, no header, no Close button — so
 * StageEditModal.tsx can show the same read-only recap inside its own
 * header (which additionally carries an "✎ Edit" action StageDataModal
 * itself never offers) without duplicating this rendering logic.
 */
export function StageDataContent({ stageKey, stageEntry, visit, patient }: Pick<StageDataModalProps, 'stageKey' | 'stageEntry' | 'visit' | 'patient'>) {
  const data = stageEntry?.stage_data || {};
  const entries = Object.entries(data).filter(([, v]) => !isEmptyValue(v));
  const usePreConsultView = stageKey === 'pre_consult';
  const useStageSummaryView = HAS_SUMMARY_VIEW.has(stageKey) && entries.length > 0;

  return (
    <>
      {stageEntry && (
        <div style={{ fontSize:12, color:'#6B7280', marginBottom:14, display:'flex', gap:14, flexWrap:'wrap' }}>
          {stageEntry.completed_by && <span>By: <b>{stageEntry.completed_by}</b></span>}
          {stageEntry.completed_at && <span>At: <b>{new Date(stageEntry.completed_at).toLocaleString('en-IN')}</b></span>}
        </div>
      )}

      {usePreConsultView ? (
        <PreConsultSummaryView data={data} />
      ) : useStageSummaryView ? (
        <StageSummaryView stageKey={stageKey} data={data} visit={visit} patient={patient} />
      ) : (
        <>
          {entries.length === 0 && (
            <div style={{ textAlign:'center', color:'#9CA3AF', fontSize:13, padding:20 }}>No data was recorded for this stage.</div>
          )}

          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {entries.map(([key, value]) => (
              <div key={key} style={{ padding:'10px 12px', background:'#F9FAFB', border:'1px solid #F3F4F6', borderRadius:'var(--radius)' }}>
                <div style={{ fontSize:11.5, fontWeight:700, letterSpacing:'.4px', color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>
                  {humanize(key)}
                </div>
                <div style={{ fontSize:13, color:'#111827' }}><RenderValue value={value} /></div>
              </div>
            ))}
          </div>
        </>
      )}

      {stageEntry?.notes && (
        <div style={{ marginTop:14, padding:'10px 12px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'var(--radius)' }}>
          <div style={{ fontSize:11.5, fontWeight:700, color:'#92400E', textTransform:'uppercase', marginBottom:4 }}>Notes</div>
          <div style={{ fontSize:13, color:'#78350F' }}>{stageEntry.notes}</div>
        </div>
      )}
    </>
  );
}

export default function StageDataModal({ stageKey, stageLabel, stageIcon, stageEntry, visit, patient, onClose }: StageDataModalProps) {
  return (
    <div onClick={onClose} style={{
      position:'fixed', inset:0, background:'rgba(17,24,39,.45)', zIndex:200,
      display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'40px 20px', overflowY:'auto',
    }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{ maxWidth:640, width:'100%', padding:0 }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:16, fontWeight:700 }}>{stageIcon} {stageLabel} — stage details</div>
          <button className="btn-ghost" style={{ padding:'4px 10px', fontSize:12 }} onClick={onClose}>✕ Close</button>
        </div>

        <div style={{ padding:'16px 20px' }}>
          <StageDataContent stageKey={stageKey} stageEntry={stageEntry} visit={visit} patient={patient} />
        </div>
      </div>
    </div>
  );
}
